/**
 * NotificationsView — shared ops inbox (not per-user).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Snackbar,
  Stack,
  TablePagination,
  Typography,
} from "@mui/material";
import { useNotifications } from "../context/NotificationContext.jsx";
import * as notificationService from "../services/notificationService.js";
import NotificationInboxStats from "../components/NotificationInboxStats.jsx";
import NotificationCategoryTabs from "../components/NotificationCategoryTabs.jsx";
import NotificationFilters from "../components/NotificationFilters.jsx";
import NotificationFeed from "../components/NotificationFeed.jsx";
import { getNotificationPath, INBOX_TABS } from "../utils/taxonomy.js";
import { useDeviceType } from "../../leads/hooks/useDeviceType.js";
import {
  PageShell,
  PageHeader,
} from "@/shared/components/pageShell";

const DEFAULT_FILTERS = {
  category: "all",
  search: "",
  priority: "",
  isRead: undefined,
};

const validCategories = new Set(INBOX_TABS.map((t) => t.id));

const NotificationsView = () => {
  const navigate = useNavigate();
  const { isMobile } = useDeviceType();
  const [searchParams] = useSearchParams();
  const {
    stats,
    loading: contextLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadStats,
    refresh,
  } = useNotifications();

  const initialCategory = validCategories.has(searchParams.get("category"))
    ? searchParams.get("category")
    : "all";

  const [filters, setFilters] = useState({
    ...DEFAULT_FILTERS,
    category: initialCategory,
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const queryFilters = useMemo(
    () => ({
      category: filters.category === "all" ? undefined : filters.category,
      search: filters.search || undefined,
      priority: filters.priority || undefined,
      isRead: filters.isRead,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }),
    [filters, page, rowsPerPage]
  );

  const loadInbox = useCallback(async () => {
    setLoading(true);
    try {
      const result = await notificationService.getNotifications(queryFilters);
      if (result?.success) {
        setNotifications(result.notifications || []);
        setTotal(result.total ?? result.count ?? 0);
      } else {
        setSnackbar({
          open: true,
          message: result?.error || "Failed to load notifications",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Failed to load notifications",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [queryFilters]);

  useEffect(() => {
    const handle = setTimeout(() => {
      loadInbox();
    }, filters.search ? 250 : 0);
    return () => clearTimeout(handle);
  }, [loadInbox, filters.search]);

  const tabCounts = useMemo(
    () => ({
      all: stats?.byCategory?.all ?? stats?.total ?? 0,
      action_required:
        stats?.actionRequired ?? stats?.byCategory?.action_required ?? 0,
      leads: stats?.byCategory?.leads ?? 0,
      quotes: stats?.byCategory?.quotes ?? 0,
      payments: stats?.byCategory?.payments ?? 0,
      operations: stats?.byCategory?.operations ?? 0,
      system: stats?.byCategory?.system ?? 0,
    }),
    [stats]
  );

  const handleOpen = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id);
      await loadStats();
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notification.id
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );
    }

    const path = getNotificationPath(notification);
    if (path) {
      navigate(path);
    }
  };

  const handleMarkRead = async (id) => {
    await markAsRead(id);
    await loadStats();
    setNotifications((prev) =>
      prev.map((n) =>
        n.id === id
          ? { ...n, isRead: true, readAt: new Date().toISOString() }
          : n
      )
    );
  };

  const handleDelete = async (id) => {
    await deleteNotification(id);
    await loadStats();
    await loadInbox();
    refresh();
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      await loadStats();
      await loadInbox();
      refresh();
      setSnackbar({
        open: true,
        message: "All notifications marked as read",
        severity: "success",
      });
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to mark all as read",
        severity: "error",
      });
    }
  };

  const handleCategoryChange = (category) => {
    setPage(0);
    setFilters((prev) => ({ ...prev, category }));
  };

  const handleFiltersChange = (next) => {
    setPage(0);
    setFilters(next);
  };

  const handleClearFilters = () => {
    setPage(0);
    setFilters({ ...DEFAULT_FILTERS, category: filters.category });
  };

  const hasActiveFilters =
    Boolean(filters.search) ||
    Boolean(filters.priority) ||
    filters.isRead !== undefined;

  return (
    <PageShell sx={{ maxWidth: "xl", mx: "auto" }}>
        <Stack spacing={isMobile ? 1.5 : 3}>
          <PageHeader
            title="Notifications"
            subtitle={
              isMobile
                ? undefined
                : "Shared ops inbox — prioritized, organized, and actionable."
            }
          />

          <NotificationInboxStats
            stats={stats}
            loading={contextLoading && !stats?.total}
            compact={isMobile}
          />

          <Box
            sx={{
              overflow: "hidden",
              borderTop: "1px solid",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <NotificationCategoryTabs
              value={filters.category}
              onChange={handleCategoryChange}
              counts={tabCounts}
              compact={isMobile}
            />
            <NotificationFilters
              filters={filters}
              onChange={handleFiltersChange}
              onClear={handleClearFilters}
              onMarkAllRead={handleMarkAllRead}
              unreadCount={stats?.unread || 0}
              compact={isMobile}
            />
            <NotificationFeed
              notifications={notifications}
              loading={loading}
              onOpen={handleOpen}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
              onSelectCategory={handleCategoryChange}
              isMobile={isMobile}
              emptyMessage={
                hasActiveFilters || filters.category !== "all"
                  ? "No notifications match your filters"
                  : "No notifications yet"
              }
            />
            {isMobile ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 1.5, py: 1, borderTop: "1px solid", borderColor: "divider" }}
              >
                <Typography variant="caption" color="text.secondary">
                  {total === 0
                    ? "0 results"
                    : `${page * rowsPerPage + 1}–${Math.min(
                        (page + 1) * rowsPerPage,
                        total
                      )} of ${total}`}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    disabled={page === 0}
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                  >
                    Prev
                  </Button>
                  <Button
                    size="small"
                    disabled={(page + 1) * rowsPerPage >= total}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_e, next) => setPage(next)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Per page"
              />
            )}
          </Box>

          {!isMobile && (
            <Alert severity="info" variant="outlined">
              Tip: Use categories and filters to focus on what the team needs to
              act on. This inbox is shared across ops — not personal.
            </Alert>
          )}
        </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageShell>
  );
};

export default NotificationsView;
